import { Form } from '@payaca/ambrosia';
import { useDebounceCallback } from '@payaca/hooks';
import { isEqual } from 'lodash-es';
import {
  ComponentPropsWithoutRef,
  PropsWithChildren,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';

const DEFAULT_DEBOUNCE_MS = 500;

const noop = () => {};
const identity = <T,>(value: T): T => value;

export type AutoSaveFormHandle = {
  /** Immediately fire any pending debounced save. */
  flush: () => void;
  /** Drop any pending debounced save without firing it. */
  cancel: () => void;
  /** Cancel pending saves and treat the current form values as saved. */
  resetBaseline: () => void;
};

export type AutoSaveFormProps<TFieldValues extends FieldValues = FieldValues> =
  {
    methods: UseFormReturn<TFieldValues>;
    /**
     * Debounced auto-save callback, fired with the latest form values once they
     * settle (after `debounceMs`). Values that fail the form's validation
     * (resolver or field rules) are not saved. No-op changes — those that leave
     * the {@link AutoSaveFormProps.getComparable comparable value} unchanged
     * since the last save — are also skipped.
     */
    onSubmit: (values: TFieldValues) => void;
    /** Debounce window in milliseconds. Defaults to 500. */
    debounceMs?: number;
    /**
     * Per-field debounce windows in milliseconds, overriding `debounceMs` when
     * that field is the latest to change.
     */
    fieldDebounceMs?: Partial<Record<FieldPath<TFieldValues>, number>>;
    /**
     * Derives the value used to decide whether anything worth saving changed since
     * the last save. Defaults to the whole form value. Provide this when the
     * persisted payload is a transform of the form value (e.g. a cleaned schedule)
     * so edits that don't affect the payload don't trigger a redundant save.
     */
    getComparable?: (values: TFieldValues) => unknown;
    /**
     * Reports whether the form holds changes not yet handed to `onSubmit` —
     * still settling in the debounce window, or held back for failing
     * validation. Use this rather than `formState.isDirty`, which tracks the
     * default values instead of what was last saved.
     */
    onUnsavedChange?: (hasUnsavedChanges: boolean) => void;
    /** Imperative handle for flushing, cancelling and resetting the save baseline. */
    ref?: Ref<AutoSaveFormHandle>;
  } & Omit<ComponentPropsWithoutRef<'form'>, 'onSubmit' | 'onError'>;

/**
 * A {@link Form} that persists changes via a debounced auto-save instead of an
 * explicit submit. Subscribes to form value changes, debounces them, skips
 * invalid and no-op saves and flushes any pending save on unmount so the last
 * valid edit is never lost.
 *
 * The form has no submit button; its native submit is suppressed.
 */
const AutoSaveForm = <TFieldValues extends FieldValues = FieldValues>(
  props: PropsWithChildren<AutoSaveFormProps<TFieldValues>>
) => {
  const {
    methods,
    onSubmit,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    fieldDebounceMs,
    getComparable,
    onUnsavedChange,
    ref,
    children,
    ...rest
  } = props;

  // Track the last saved comparable rather than the form's `dirtyFields`: those
  // flip back when the user edits to the original value, which would otherwise
  // drop the "revert" save. Seeded lazily from the initial values so an
  // immediate subscription fire (or a change that nets back to the start) is a
  // no-op.
  const lastComparableRef = useRef<{ value: unknown } | null>(null);
  if (lastComparableRef.current === null) {
    lastComparableRef.current = {
      value: (getComparable ?? identity)(methods.getValues()),
    };
  }

  // Kept current on every render so the subscription below can report against
  // the latest props without resubscribing.
  const reportUnsavedRef = useRef<() => boolean>(() => false);
  reportUnsavedRef.current = () => {
    const hasUnsavedChanges = !isEqual(
      (getComparable ?? identity)(methods.getValues()),
      lastComparableRef.current?.value
    );
    onUnsavedChange?.(hasUnsavedChanges);
    return hasUnsavedChanges;
  };

  const debouncedSave = useDebounceCallback(
    (_changedField?: string) => {
      // handleSubmit runs the form's validation (resolver or field rules) and
      // only calls through when it passes, so invalid states are never saved.
      void methods.handleSubmit((values) => {
        const comparable = (getComparable ?? identity)(values);
        if (isEqual(comparable, lastComparableRef.current?.value)) return;
        lastComparableRef.current = { value: comparable };
        reportUnsavedRef.current();
        onSubmit(values);
      })();
    },
    (changedField?: string) => {
      const fieldDelays: Partial<Record<string, number>> | undefined =
        fieldDebounceMs;
      return (
        (changedField ? fieldDelays?.[changedField] : undefined) ?? debounceMs
      );
    },
    { flushOnUnmount: true }
  );

  const { subscribe } = methods;
  useEffect(() => {
    const unsubscribe = subscribe({
      formState: { values: true },
      callback: ({ name }) => {
        if (reportUnsavedRef.current()) {
          debouncedSave(name);
        } else {
          debouncedSave.cancel();
        }
      },
    });
    return () => {
      unsubscribe();
      debouncedSave.cancel();
    };
  }, [subscribe, debouncedSave]);

  useImperativeHandle(
    ref,
    () => ({
      flush: () => debouncedSave.flush(),
      cancel: () => debouncedSave.cancel(),
      resetBaseline: () => {
        debouncedSave.cancel();
        lastComparableRef.current = {
          value: (getComparable ?? identity)(methods.getValues()),
        };
        reportUnsavedRef.current();
      },
    }),
    [debouncedSave, getComparable, methods]
  );

  return (
    <Form methods={methods} onSubmit={noop} {...rest}>
      {children}
    </Form>
  );
};

export default AutoSaveForm;
